/**
 * Auth API Client
 *
 * API functions for authentication operations
 */

import type {
  RegisterRequest,
  RegisterResponse,
  EmailVerificationResponse,
  LoginRequest,
  LoginResponse,
} from '@hummii/shared-types';
import { buildApiUrl, handleApiResponse, ApiError } from '@shared/api';

/**
 * Start Google OAuth flow.
 *
 * OAuth must run as a full-page navigation (not fetch): the browser is sent to
 * the API, which redirects to Google and back to `${FRONTEND_URL}/auth/callback`
 * after setting HTTP-only auth cookies. The callback page then calls getMe().
 */
export function loginWithGoogle(): void {
  window.location.href = buildApiUrl('/auth/google');
}

/**
 * Get current session (user + actors) using the HTTP-only auth cookies.
 * Used by the OAuth callback page to hydrate the auth store, since the OAuth
 * redirect returns no JSON body (tokens live only in cookies).
 */
export async function getMe(): Promise<LoginResponse> {
  const response = await fetch(buildApiUrl('/auth/me'), {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<LoginResponse>(response);
}

/**
 * Consent payload for completing an OAuth registration (PIPEDA).
 */
export interface OAuthConsentRequest {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  ageVerified: boolean;
  marketingConsent?: boolean;
}

/**
 * Complete an OAuth registration after the user gives explicit consent.
 * The consent token lives in an HTTP-only cookie set during the OAuth callback;
 * on success the backend creates the account and issues the real session cookies.
 */
export async function completeOAuthRegistration(
  consent: OAuthConsentRequest
): Promise<LoginResponse> {
  const response = await fetch(buildApiUrl('/auth/oauth/complete'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // send the consent cookie, receive session cookies
    body: JSON.stringify(consent),
  });

  return handleApiResponse<LoginResponse>(response);
}

/**
 * Register new user
 */
export async function registerUser(
  data: RegisterRequest
): Promise<RegisterResponse> {
  const url = buildApiUrl('/auth/register');
  console.log('[auth.api] registerUser - URL:', url);
  console.log('[auth.api] registerUser - Data:', { ...data, password: '***HIDDEN***' });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for CORS
      body: JSON.stringify(data),
    });

    console.log('[auth.api] registerUser - Response status:', response.status);
    console.log('[auth.api] registerUser - Response ok:', response.ok);

    const result = await handleApiResponse<RegisterResponse>(response);
    console.log('[auth.api] registerUser - Success:', result);
    return result;
  } catch (error) {
    console.error('[auth.api] registerUser - Error:', error);
    throw error;
  }
}

/**
 * Verify email with token
 */
export async function verifyEmail(
  token: string
): Promise<EmailVerificationResponse> {
  const response = await fetch(
    buildApiUrl(`/auth/verify-email?token=${encodeURIComponent(token)}`),
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  return handleApiResponse<EmailVerificationResponse>(response);
}

/**
 * Login user
 */
export async function loginUser(
  data: LoginRequest
): Promise<LoginResponse> {
  const response = await fetch(buildApiUrl('/auth/login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important: allows cookies to be sent/received
    body: JSON.stringify(data),
  });

  return handleApiResponse<LoginResponse>(response);
}

/**
 * Logout user
 */
export async function logoutUser(): Promise<void> {
  const response = await fetch(buildApiUrl('/auth/logout'), {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ApiError('Logout failed', response.status);
  }
}

/**
 * Resend verification email
 */
export async function resendVerificationEmail(
  email: string
): Promise<{ message: string }> {
  const response = await fetch(buildApiUrl('/auth/resend-verification'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  return handleApiResponse<{ message: string }>(response);
}

/**
 * Change password for authenticated user
 * Requires current password verification
 * Invalidates all sessions after successful change
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export async function changePassword(
  data: ChangePasswordRequest
): Promise<ChangePasswordResponse> {
  const response = await fetch(buildApiUrl('/auth/change-password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important: JWT in HTTP-only cookie
    body: JSON.stringify(data),
  });

  return handleApiResponse<ChangePasswordResponse>(response);
}
